import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionBackground from './SectionBackground';

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: 'Enter property details',
      description: 'Add address, rent amount, and lease terms',
      icon: '🏠'
    },
    {
      title: 'Add tenant information',
      description: 'Enter tenant names and contact details',
      icon: '👤'
    },
    {
      title: 'Customize clauses',
      description: 'Select utilities, pets, and other terms',
      icon: '📝'
    },
    {
      title: 'Review & generate',
      description: 'Get your California-focused lease draft',
      icon: '✓'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [steps.length]);

  const goToSignup = () => {
    const trimmedEmail = email.trim();
    if (trimmedEmail.length > 0) {
      navigate(`/signup?email=${encodeURIComponent(trimmedEmail)}`);
      return;
    }

    navigate('/signup');
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    goToSignup();
  };

  return (
    <SectionBackground 
      ref={containerRef}
      variant="white"
      className="hero-section"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 0 40px',
        background: 'var(--cb-white)'
      }}
    >
      <div className="cb-container" style={{
        position: 'relative',
        zIndex: 1
      }}>
        {/* Coinbase-style Hero Layout */}
        <div className="cb-grid-2 hero-main-grid" style={{
          marginBottom: 'var(--cb-space-20)'
        }}>
          {/* Mobile Phone Mockups */}
          <div className="hero-mobile-mockup" style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            alignItems: 'flex-start'
          }}>
            {/* Success Phone - Static */}
            <div className="cb-phone-mockup cb-phone-mockup-sm">
              <div className="cb-phone-screen" style={{
                background: '#f8f9fa'
              }}>
                {/* Success Screen Content */}
                <div className="cb-phone-content" style={{
                  padding: '30px 16px'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'var(--cb-primary-100)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                  <div style={{
                    fontSize: '24px',
                    color: 'white'
                  }}>✓</div>
                </div>

                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'var(--cb-font-weight-bold)',
                  color: 'var(--cb-dark-gray)',
                  marginBottom: '8px',
                  fontFamily: 'var(--cb-font-family)'
                }}>
                  Lease created successfully!
                </h3>

                <p style={{
                  fontSize: '11px',
                  color: 'var(--cb-light-gray)',
                  marginBottom: '20px',
                  lineHeight: 'var(--cb-line-height-normal)',
                  fontFamily: 'var(--cb-font-family)'
                }}>
                  Your California-focused lease draft is ready
                </p>

                <div style={{
                  background: 'white',
                  borderRadius: '10px',
                  padding: '12px',
                  width: '100%',
                  marginBottom: '16px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    color: '#5B616E',
                    marginBottom: '6px'
                  }}>
                    123 Main St, LA
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#0A0B0D'
                  }}>
                    Monthly Rent: $2,500
                  </div>
                </div>

                <button
                  type="button"
                  onClick={goToSignup}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#0052FF',
                    color: 'white',
                    border: 'none',
                    borderRadius: '999px',
                    fontSize: '13px',
                    fontWeight: 600,
                    marginBottom: '10px'
                  }}
                >
                  Download PDF
                </button>

                <button
                  type="button"
                  onClick={goToSignup}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'transparent',
                    color: '#0052FF',
                    border: '2px solid #0052FF',
                    borderRadius: '999px',
                    fontSize: '13px',
                    fontWeight: 600
                  }}
                >
                  Send to tenant
                </button>
                </div>
              </div>
            </div>

            {/* Steps Phone - Cycles through steps */}
            <div className="cb-phone-mockup cb-phone-mockup-sm" style={{
              marginTop: '40px'
            }}>
              <div className="cb-phone-screen" style={{
                background: '#f8f9fa'
              }}>
                <div className="cb-phone-content">
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'var(--cb-primary-100)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 'var(--cb-space-6)',
                    transition: 'all 0.5s ease'
                  }}>
                    <div style={{
                      fontSize: '32px',
                      color: 'white'
                    }}>{steps[currentStep].icon}</div>
                  </div>

                  <h3 style={{
                    fontSize: 'var(--cb-text-xl)',
                    fontWeight: 'var(--cb-font-weight-bold)',
                    color: 'var(--cb-dark-gray)',
                    marginBottom: 'var(--cb-space-3)',
                    fontFamily: 'var(--cb-font-family)',
                    transition: 'all 0.5s ease'
                  }}>
                    {steps[currentStep].title}
                  </h3>

                  <p style={{
                    fontSize: 'var(--cb-text-sm)',
                    color: 'var(--cb-light-gray)',
                    marginBottom: 'var(--cb-space-6)',
                    lineHeight: 'var(--cb-line-height-normal)',
                    fontFamily: 'var(--cb-font-family)',
                    transition: 'all 0.5s ease'
                  }}>
                    {steps[currentStep].description}
                  </p>

                  {/* Progress indicators */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center',
                    marginTop: '32px'
                  }}>
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: index === currentStep ? '#0052FF' : '#E5E7EB',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Content */}
          <div className="hero-content" style={{ maxWidth: '560px', margin: '0 auto' }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
              fontWeight: 700,
              lineHeight: 1.1,
              marginBottom: '24px',
              color: '#0A0B0D',
              letterSpacing: '-0.02em'
            }}>
              The easiest way to
              <br />
              <span style={{ color: '#0052FF' }}>create California leases</span>
            </h1>

            <p style={{
              fontSize: 'clamp(1.2rem, 2.6vw, 1.45rem)',
              color: '#3A3F4C',
              lineHeight: 1.7,
              marginBottom: '48px'
            }}>
              Generate a California-focused lease draft in minutes with guided steps and common disclosures.
              <span style={{ color: '#0052FF', fontWeight: 700 }}>&nbsp;For free.</span>
            </p>

            <form
              onSubmit={handleSubmit}
              className="hero-email-form"
              style={{
                display: 'flex',
                gap: '16px',
                maxWidth: '560px',
                width: '100%',
                margin: '0 auto',
                alignItems: 'flex-end',
                justifyContent: 'center',
                textAlign: 'left'
              }}
            >
              <div style={{
                flex: 1
              }}>
                <label
                  htmlFor="hero-email"
                  style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#3A3F4C'
                  }}
                >
                  Email address
                </label>
                <input
                  id="hero-email"
                  type="text"
                  placeholder="landlord@example.com"
                  style={{
                    width: '100%',
                    height: '56px',
                    padding: '0 28px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    fontSize: '1.05rem',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit'
                  }}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#0052FF';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 82, 255, 0.12)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
              <button
                type="submit"
                style={{
                  height: '56px',
                  padding: '0 40px',
                  background: '#0052FF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '999px',
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#0041CC';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#0052FF';
                }}
              >
                Get started
              </button>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .hero-section {
            padding: 100px 20px 60px !important;
            min-height: 100vh !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          .hero-main-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          
          .hero-content {
            order: 1 !important;
            text-align: center !important;
            padding: 0 12px !important;
          }
          
          .hero-mobile-mockup {
            display: none !important;
          }
          
          .hero-content h1 {
            font-size: 2.8rem !important;
            line-height: 1.22 !important;
            margin-bottom: 24px !important;
          }
          
          .hero-content p {
            font-size: 1.3rem !important;
            line-height: 1.75 !important;
            margin-bottom: 32px !important;
          }
          
          .hero-email-form {
            flex-direction: column !important;
            gap: 16px !important;
            width: 100% !important;
            max-width: 100% !important;
            align-items: stretch !important;
          }

          .hero-email-form label {
            font-size: 1rem !important;
            text-align: left !important;
          }

          .hero-email-form button {
            width: 100% !important;
            height: 56px !important;
            font-size: 1.15rem !important;
          }
        }

        @media (max-width: 480px) {
          .hero-section {
            padding: 100px 16px 48px !important;
            min-height: 100vh !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
          }
          
          .hero-main-grid {
            gap: 30px !important;
          }
          
          .hero-content {
            padding: 0 12px !important;
          }
          
          .hero-content h1 {
            font-size: 2.7rem !important;
            line-height: 1.24 !important;
            margin-bottom: 24px !important;
          }
          
          .hero-content p {
            font-size: 1.25rem !important;
            line-height: 1.78 !important;
            margin-bottom: 30px !important;
          }

          .hero-email-form {
            flex-direction: column !important;
            gap: 16px !important;
            width: 100% !important;
            max-width: 100% !important;
            align-items: stretch !important;
          }

          .hero-email-form label {
            font-size: 0.95rem !important;
            text-align: left !important;
          }
          
          .hero-email-form button {
            height: 56px !important;
            font-size: 1.15rem !important;
            width: 100% !important;
          }
          
          .hero-mobile-mockup {
            display: none !important;
          }
        }
      `}</style>
    </SectionBackground>
  );
};

export default HeroSection; 
